import styled from "@emotion/styled";
import {
  ATTORNEY_SERVICES_CARD_BACKGROUND_COLOR_GRADIENT,
  BORDER_RADIUS_DEFAULT,
  COLOR_PURPLE_TEXT,
  COLOR_TERTIARY,
  FONT_SIZE_XLARGE,
  HEADER_Z_INDEX,
  MOBILE_SCREEN,
  COLOR_APP_TEXT,
} from "../theme";
import {
  Paragraph,
  ParagraphHeader,
  ParagraphHeaderXLarge,
  ParagraphSmall,
} from "../typography/Paragraph";
import { DocumentWithRelations, User } from "../../../../api/src/types";
import { useRef, useEffect } from "react";
import { AnswersWithAttorneyRestrictionsGetResponse } from "../../../../api/src/users/answers/route";
import { ProductsGetResponse } from "../../../../api/src/products/route";
import {
  AttorneyServiceItem,
  getAttorneyServicesItems,
} from "./utils/attorney-services";
import { PrimaryButton } from "../buttons/PrimaryButton";
import { PurchaseItem, Product } from "../../../../api/src/types";
import { CartGetResponse } from "../../../../api/src/users/carts/route";
import ErrorMessage from "../Error";
import { useNavigate } from "react-router";
import {
  useCreateCartMutation,
  useAddCartItemMutation,
  useRemoveCartItemMutation,
} from "../../api/payments";
import Loading from "../Loading";

const StyledParagraphHeaderXLarge = styled(ParagraphHeaderXLarge)`
  color: ${COLOR_PURPLE_TEXT};
  font-weight: 500;
  ${MOBILE_SCREEN} {
    font-size: ${FONT_SIZE_XLARGE};
  }
`;

const AttorneyServicesCardContainer = styled.div`
  position: fixed;
  display: flex;
  flex-direction: column;
  gap: 8px;
  top: 64px;
  right: 48px;
  max-width: 352px;
  width: 100%;
  border-radius: ${BORDER_RADIUS_DEFAULT};
  padding: 32px 24px;
  background: ${ATTORNEY_SERVICES_CARD_BACKGROUND_COLOR_GRADIENT};
  z-index: ${HEADER_Z_INDEX};
  ${MOBILE_SCREEN} {
    right: auto;
    left: 50%;
    transform: translateX(-50%);
    max-width: calc(100vw - 80px);
  }
`;

const AttorneyServicesItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-radius: ${BORDER_RADIUS_DEFAULT};
  padding: 16px;
  background: ${COLOR_TERTIARY};
`;

interface AttorneyServicesCardProps {
  user: User;
  userDocument: DocumentWithRelations;
  products: ProductsGetResponse;
  carts: CartGetResponse;
  setIsOpen: (isOpen: boolean) => void;
  buttonRef: React.RefObject<HTMLButtonElement>;
  answersWithAttorneyRestrictions: AnswersWithAttorneyRestrictionsGetResponse;
  representationPayment: (PurchaseItem & { product: Product }) | undefined;
  userCanPurchaseRepresentation: boolean;
  fianceCanPurchaseRepresentation: boolean;
  maxRepresentationProductsCanPurchase: number;
  qAPayment: (PurchaseItem & { product: Product }) | undefined;
  isQAPending: boolean;
  isQAProductAvailable: boolean;
}

const AttorneyServicesCard: React.FC<AttorneyServicesCardProps> = ({
  user,
  carts,
  products,
  userDocument,
  setIsOpen,
  buttonRef,
  answersWithAttorneyRestrictions,
  representationPayment,
  userCanPurchaseRepresentation,
  fianceCanPurchaseRepresentation,
  maxRepresentationProductsCanPurchase,
  qAPayment,
  isQAPending,
  isQAProductAvailable,
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [createCart, { isLoading: isCreatingCart, error: createCartError }] =
    useCreateCartMutation();
  const [
    removeCartItem,
    { isLoading: isRemovingCartItem, error: removeCartItemError },
  ] = useRemoveCartItemMutation();
  const [
    addCartItem,
    { isLoading: isAddingCartItem, error: addCartItemError },
  ] = useAddCartItemMutation();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        cardRef.current &&
        !cardRef.current.contains(event.target as Node) &&
        !buttonRef.current?.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [setIsOpen, buttonRef]);

  const isLoading = isCreatingCart || isAddingCartItem || isRemovingCartItem;
  const processingError =
    createCartError ?? addCartItemError ?? removeCartItemError;
  if (isLoading) {
    return (
      <AttorneyServicesCardContainer ref={cardRef}>
        <StyledParagraphHeaderXLarge>
          Hi {user.firstName},
        </StyledParagraphHeaderXLarge>
        <ParagraphHeader fontWeight={500}>
          Want Attorney Services?
        </ParagraphHeader>
        <AttorneyServicesItemContainer>
          <Loading />
        </AttorneyServicesItemContainer>
      </AttorneyServicesCardContainer>
    );
  }
  if (processingError) {
    return (
      <AttorneyServicesCardContainer ref={cardRef}>
        <StyledParagraphHeaderXLarge>
          Hi {user.firstName},
        </StyledParagraphHeaderXLarge>
        <ParagraphHeader fontWeight={500}>
          Want Attorney Services?
        </ParagraphHeader>
        <AttorneyServicesItemContainer>
          <ErrorMessage>
            Error ocurred. Please refresh the page or contact support.
          </ErrorMessage>
        </AttorneyServicesItemContainer>
      </AttorneyServicesCardContainer>
    );
  }

  const handleButtonClick = async (item: AttorneyServiceItem) => {
    try {
      switch (item.buttonAction) {
        case "BOOK":
          void navigate(`/attorney-services?serviceType=${item.serviceType}`);
          setIsOpen(false);
          break;
        case "PURCHASE":
          if (carts.length === 0) {
            const cart = await createCart({
              id: user.id,
              cartStatus: "UNPAID",
            }).unwrap();
            await addCartItem({
              userId: user.id,
              cartId: cart.id,
              productId: item.productId,
              quantity: item.quantity,
            }).unwrap();
          } else {
            const cart = carts[0];
            if (!cart) {
              throw new Error("Cart not found");
            }
            const hasServiceCartItem = cart.cartItems.some(
              (cartItem) =>
                cartItem.product.productType === item.serviceType &&
                cartItem.deletedAt === null,
            );
            if (!hasServiceCartItem) {
              await addCartItem({
                userId: user.id,
                cartId: cart.id,
                productId: item.productId,
                quantity: item.quantity,
              }).unwrap();
              // when adding a representation item, we need to remove the notarization item if it exists in the cart
              const notarizationCartItem = cart.cartItems.find(
                (item) =>
                  item.product.productType === "NOTARIZATION" &&
                  item.deletedAt === null,
              );
              if (
                item.serviceType === "REPRESENTATION" &&
                notarizationCartItem
              ) {
                await removeCartItem({
                  userId: user.id,
                  cartId: cart.id,
                  cartItemId: notarizationCartItem.id,
                }).unwrap();
              }
            }
          }
          void navigate(`/payment`);
          setIsOpen(false);
          break;
      }
    } catch (error) {
      console.error(error);
    }
  };

  const { items, error } = getAttorneyServicesItems({
    userDocument,
    answersWithAttorneyRestrictions,
    products,
    representationPayment,
    qAPayment,
    isQAPending,
    isQAProductAvailable,
    userCanPurchaseRepresentation,
    fianceCanPurchaseRepresentation,
    maxRepresentationProductsCanPurchase,
  });
  if (items.length === 0) {
    return (
      <AttorneyServicesCardContainer ref={cardRef}>
        <StyledParagraphHeaderXLarge>
          Hi {user.firstName},
        </StyledParagraphHeaderXLarge>
        <ParagraphHeader fontWeight={500}>
          Want Attorney Services?
        </ParagraphHeader>
        <AttorneyServicesItemContainer>
          <ErrorMessage
            {...(error && { color: COLOR_APP_TEXT, align: "left" })}
          >
            {error ?? "No services available. Please contact support."}
          </ErrorMessage>
        </AttorneyServicesItemContainer>
      </AttorneyServicesCardContainer>
    );
  }

  return (
    <AttorneyServicesCardContainer
      ref={cardRef}
      data-testid="attorney-services-card"
    >
      <StyledParagraphHeaderXLarge>
        Hi {user.firstName},
      </StyledParagraphHeaderXLarge>
      <ParagraphHeader fontWeight={500}>
        Want Attorney Services?
      </ParagraphHeader>
      {error && (
        <AttorneyServicesItemContainer>
          <ErrorMessage
            {...(error && { color: COLOR_APP_TEXT, align: "left" })}
          >
            {error}
          </ErrorMessage>
        </AttorneyServicesItemContainer>
      )}
      {items.map((item) => (
        <AttorneyServicesItemContainer key={item.serviceType}>
          <Paragraph fontWeight={500}>{item.serviceName}</Paragraph>
          <Paragraph fontWeight={300}>{item.serviceDescription}</Paragraph>
          <PrimaryButton
            data-testid={`attorney-services-card-button-${item.serviceType}`}
            onClick={async () => {
              await handleButtonClick(item);
            }}
          >
            {item.buttonText}
          </PrimaryButton>
          {item.serviceFooterText && (
            <ParagraphSmall fontWeight={300}>
              {item.serviceFooterText}
            </ParagraphSmall>
          )}
        </AttorneyServicesItemContainer>
      ))}
    </AttorneyServicesCardContainer>
  );
};

export default AttorneyServicesCard;
